import type {
  CatalogPlan,
  LocalizedText,
  Locale,
  PlanInterval,
  PlanTier,
  UpgradeQuote,
} from '../../api/types';

/** FR-BIL-16: billing dates (periods, renewals, AI quota months) are in Taiwan time. */
export const ACCOUNT_TZ = 'Asia/Taipei';

/**
 * Plan catalog helpers (§5.13). Prices and plan names always come from the API catalog
 * (FR-BIL-04); nothing here knows an amount. The tier order and the upgrade/downgrade rules
 * mirror smoke-backend (FR-BIL-14): an upgrade is a higher tier; anything lower is a downgrade at
 * the end of the period. Paid plans bill monthly only.
 */

export const TIER_RANK: Record<PlanTier, number> = { free: 0, pro: 1, agency: 2 };

/** Minimal plan identity shared by the catalog and the billing overview. */
export interface PlanLike {
  code: string;
  tier: PlanTier;
  interval: PlanInterval | null;
}

/** Whole New Taiwan dollars, as Aircraft Pay charges them (TWD only). */
export function formatPrice(amount: number, locale: Locale, currency = 'TWD'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: currency === 'TWD' ? 'symbol' : 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** A text the API sends in both languages (plan names, FR-BIL-12/13 notices). */
export function localized(text: LocalizedText | null | undefined, locale: Locale): string {
  if (!text) return '';
  return text[locale] || text.en;
}

export type PlanChange = 'current' | 'purchase' | 'upgrade' | 'downgrade';

/** What choosing `target` means for a workspace on `current`. */
export function planChange(current: PlanLike, target: PlanLike): PlanChange {
  if (current.code === target.code) return 'current';
  const from = TIER_RANK[current.tier];
  const to = TIER_RANK[target.tier];
  if (target.tier === 'free') return 'downgrade';
  if (current.tier === 'free') return 'purchase';
  if (to > from) return 'upgrade';
  return 'downgrade';
}

/** One plan per tier, in tier order (the catalog has one plan per tier). */
export function tierColumns(items: CatalogPlan[]): CatalogPlan[] {
  return [...items].sort((a, b) => TIER_RANK[a.tier] - TIER_RANK[b.tier]);
}

export interface QuoteLine {
  key: 'proratedDifference' | 'creditApplied' | 'dueNow';
  amount: number;
}

/**
 * Lines of the prorated upgrade preview (FR-BIL-06), from the API quote:
 * the prorated difference for the rest of the period, then credit, then what is charged now.
 */
export function quoteLines(quote: UpgradeQuote): QuoteLine[] {
  const lines: QuoteLine[] = [{ key: 'proratedDifference', amount: quote.amount }];
  if (quote.creditApplied > 0) lines.push({ key: 'creditApplied', amount: -quote.creditApplied });
  lines.push({ key: 'dueNow', amount: quote.netAmount });
  return lines;
}

/** A limit for display: `null` means unlimited / fair use. */
export function limitValue(limit: number | null, locale: Locale): string | null {
  return limit === null ? null : new Intl.NumberFormat(locale).format(limit);
}

/** Share of a limit in use (0–1), or null without a limit. */
export function usageRatio(used: number, limit: number | null): number | null {
  if (limit === null || limit <= 0) return null;
  return Math.min(1, used / limit);
}
